import { useMantineTheme } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { toPng } from 'html-to-image';
import { useCallback } from 'react';

interface UseBiMetricExportParams {
	biMetricId: string;
	metricName: string;
}

export function useBiMetricExport({
	biMetricId,
	metricName,
}: UseBiMetricExportParams) {
	const theme = useMantineTheme();

	const getExportElement = useCallback(() => {
		const element = document.querySelector(
			`[data-bi-metric-export-target="${biMetricId}"]`
		) as HTMLElement | null;

		if (!element) {
			showNotification({
				title: 'Export Error',
				message: 'Could not find chart to export',
				color: 'red',
			});
			return null;
		}

		return element;
	}, [biMetricId]);

	const getElementDimensions = useCallback(
		(element: HTMLElement | SVGSVGElement) => {
			const rect = element.getBoundingClientRect();
			return {
				width: rect.width,
				height: rect.height,
			};
		},
		[]
	);

	const handleCopyAsPng = useCallback(async () => {
		try {
			const element = getExportElement();
			if (!element) return;

			const { width, height } = getElementDimensions(element);

			const dataURL = await toPng(element, {
				backgroundColor: theme.other.getColor('surface/primary/default'),
				width,
				height,
				pixelRatio: 2,
			});

			// Convert data URL to blob
			const response = await fetch(dataURL);
			const blob = await response.blob();

			// Copy to clipboard
			await navigator.clipboard.write([
				new ClipboardItem({
					[blob.type]: blob,
				}),
			]);

			showNotification({
				title: 'Success',
				message: 'Chart copied to clipboard as PNG',
				color: 'green',
			});
		} catch {
			showNotification({
				title: 'Export Error',
				message: 'Failed to copy chart to clipboard',
				color: 'red',
			});
		}
	}, [getExportElement, getElementDimensions, theme]);

	const handleDownloadPng = useCallback(async () => {
		try {
			const element = getExportElement();
			if (!element) return;

			const { width, height } = getElementDimensions(element);

			const dataURL = await toPng(element, {
				backgroundColor: theme.other.getColor('surface/primary/default'),
				width,
				height,
				pixelRatio: 2,
			});

			const downloadLink = document.createElement('a');
			downloadLink.setAttribute(
				'download',
				`${metricName || 'metric'}-chart.png`
			);
			downloadLink.setAttribute('href', dataURL);
			downloadLink.click();

			showNotification({
				title: 'Success',
				message: 'Chart downloaded as PNG',
				color: 'green',
			});
		} catch {
			showNotification({
				title: 'Export Error',
				message: 'Failed to download chart',
				color: 'red',
			});
		}
	}, [getExportElement, getElementDimensions, metricName, theme]);

	return {
		handleCopyAsPng: handleCopyAsPng,
		handleDownloadPng: handleDownloadPng,
	};
}
